import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MunicipalityUser, NotificationResponse, PassageData, PassageRequestResponse, PassageResponse, Vehicle } from '../models/agrinio';
import { environment } from '../../../environments/environment';

let IP = environment.apiUrl

let https_type = environment.apiType

@Injectable({
  providedIn: 'root'
})
export class AccessControlService {
  private http = inject(HttpClient);
  private vehiclesUrl = https_type + '://' + IP + '/api/agrinio/vehicles';
  private municipalityUsersURl = https_type + '://' + IP + '/api/agrinio/municipality/users';
  private vehiclesPassagesUrl = https_type + '://' + IP + '/api/agrinio/vehicles-passages/requests';
  private vehicleEntrancesUrl = https_type + '://' + IP + '/api/agrinio/vehicles-passages/entrances';
  private notificationsUrl = https_type + '://' + IP + '/api/agrinio/notifications';

  getVehicles(): Observable<Vehicle[]> {
    return this.http.get<{ data: Vehicle[] }>(this.vehiclesUrl)
      .pipe(map(response => response.data));
  }

  getMunicipalityUsers(): Observable<MunicipalityUser[]> {
    return this.http.get<{ data: MunicipalityUser[] }>(this.municipalityUsersURl)
      .pipe(map(response => response.data));
  }

  getMunicipalityUser(userId: number): Observable<MunicipalityUser> {
    return this.http.get<{ data: MunicipalityUser }>(`${this.municipalityUsersURl}/${userId}`)
      .pipe(map(response => response.data));
  }

  addMunicipalityUser(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/vnd.api+json'
    });

    const formattedPayload = {
      data: {
        type: 'municipality-user',
        attributes: payload
      }
    };

    return this.http.post<{ data: any }>(this.municipalityUsersURl, formattedPayload, { headers })
      .pipe(map(response => response.data));
  }

  updateMunicipalityUser(id: number, payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/vnd.api+json'
    });

    const formattedPayload = {
      data: {
        type: 'municipality-user',
        id: id.toString(),
        attributes: payload
      }
    };

    return this.http.put<{ data: any }>(`${this.municipalityUsersURl}/${id}`, formattedPayload, { headers })
      .pipe(map(response => response.data));
  }

  getVehiclePassageRequests(filters?: { status?: string[], start?: string, stop?: string, ordering?: string }): Observable<PassageRequestResponse> {
    let params = new HttpParams();

    filters?.status?.forEach(status => {
      params = params.append('status__exact', status);
    });

    params = filters?.start ? params.set('start__gte', filters.start) : params;
    params = filters?.stop ? params.set('stop__lte', filters.stop) : params;
    params = params.set('ordering', filters?.ordering || '-created_at');

    return this.http.get<PassageRequestResponse>(this.vehiclesPassagesUrl, { params });
  }

  updatePassageRequestStatus(id: string, status: 'PENDING' | 'ACCEPTED' | 'REJECTED', userId: string): Observable<void> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/vnd.api+json'
    });

    const body = {
      data: {
        type: 'passage-request',
        id: id,
        attributes: {
          status: status,
          user_id: userId,
        }
      }
    };

    return this.http.patch<void>(`${this.vehiclesPassagesUrl}/${id}`, body, { headers });
  }

  getVehicleEntrancesPassages(): Observable<PassageData[]> {
    return this.http.get<{ data: PassageResponse }>(this.vehicleEntrancesUrl)
      .pipe(map(response => response.data.data));
  }

  getNotifications(): Observable<NotificationResponse> {
    const params = new HttpParams().set('type', 'LIVE_MESSAGE');
    return this.http.get<NotificationResponse>(this.notificationsUrl, { params });
  }

  markNotificationAsSeen(uuid: string): Observable<void> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/vnd.api+json'
    });

    const body = {
      data: {
        type: 'notification',
        id: uuid,
        attributes: {
          seen_at: true
        }
      }
    };

    return this.http.patch<void>(`${this.notificationsUrl}/${uuid}`, body, { headers });
  }
}